import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Send } from "lucide-react";

const RequestInviteForm = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('invite_requests')
        .insert({
          email: email.toLowerCase().trim(),
          name: name.trim() || null,
          message: message.trim() || null,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('An invite request with this email already exists');
        }
        throw error;
      }

      setIsSubmitted(true);
      toast({
        title: "Request submitted! 🎉",
        description: "We'll review your request and send you an invite code soon.",
        duration: 6000,
      });
      
      // Reset form
      setEmail("");
      setName("");
      setMessage("");
    } catch (error: any) {
      toast({
        title: "Error submitting request",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-green-700 dark:text-green-400">Request Submitted!</CardTitle>
          <CardDescription>
            Thank you for your interest in Positivio. We'll review your request and send you an invite code to your email soon.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => setIsSubmitted(false)} 
            variant="outline" 
            className="w-full"
          >
            Submit Another Request
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>Request an Invite</CardTitle>
        <CardDescription>
          Don't have an invite code? Submit your request and we'll send you one!
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="requestEmail">Email Address *</Label>
            <Input
              id="requestEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="requestName">Your Name</Label>
            <Input
              id="requestName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="requestMessage">Why do you want to join? (Optional)</Label>
            <Textarea
              id="requestMessage"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us why you're interested in joining our positive community..."
              rows={3}
            />
          </div>
          
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Request Invite
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default RequestInviteForm;